'use client';

import { useCallback, useEffect, useState } from 'react';
import { Edit3, Keyboard, Plus, Trash2 } from 'lucide-react';
import { useBlockIrSignalStore } from '@/stores/useBlockIrSignalStore';
import { useBlockModalIrStore } from '@/stores/useBlockModalIrStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';

export type IrSignalData = {
    id: string;
    name: string;
    rawData: number[];
    rawCode: string;
    source: 'manual' | 'auto-orange' | 'auto-blue';
};

const PRESETS = [
    { value: '1', label: '信号1' },
    { value: '2', label: '信号2' },
    { value: '3', label: '信号3' },
    { value: '4', label: '信号4' },
    { value: '5', label: '信号5' },
];

export default function IrSignal() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalIrStore();
    const { signals: storedSignals, addSignal, removeSignal, updateSignal } = useBlockIrSignalStore();

    const [selectedId, setSelectedId] = useState<string | null>(() => {
        const current = fieldRef?.getSignals() ?? [];
        return current.length > 0 ? current[0].id : null;
    });
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [pendingName, setPendingName] = useState('');
    const [pendingRawText, setPendingRawText] = useState('');

    const isValidRaw = (text: string): boolean => {
        const trimmed = text.trim();
        if (!trimmed) return false;
        return trimmed.split(',').every((v) => !isNaN(Number(v.trim())) && v.trim() !== '');
    };

    const handleAdd = () => {
        if (!pendingName.trim() || !isValidRaw(pendingRawText)) return;
        const rawData = pendingRawText
            .trim()
            .split(',')
            .map((v) => Number(v.trim()));
        const newSignal: IrSignalData = {
            id: crypto.randomUUID(),
            name: pendingName.trim(),
            rawData,
            rawCode: '',
            source: 'manual',
        };
        addSignal(newSignal);
        setSelectedId(newSignal.id);
        setPendingName('');
        setPendingRawText('');
    };

    const handleOk = useCallback(() => {
        if (selectedId) {
            const preset = PRESETS.find((p) => p.value === selectedId);
            if (preset) {
                fieldRef?.setSignals([
                    { id: preset.value, name: preset.label, rawData: [], rawCode: '', source: 'manual' },
                ]);
            } else {
                const signal = storedSignals.find((s) => s.id === selectedId);
                if (signal) fieldRef?.setSignals([signal]);
            }
        } else {
            fieldRef?.setSignals([]);
        }
        closeModal();
    }, [selectedId, storedSignals, fieldRef, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    const canAdd = pendingName.trim().length > 0 && isValidRaw(pendingRawText);

    const handleRenameSubmit = (id: string) => {
        if (editingName.trim()) {
            updateSignal?.(id, { name: editingName.trim() });
        }
        setEditingId(null);
    };

    return (
        <div className="flex flex-col h-full bg-color-block-action-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 h-[50px] bg-color-block-action-modal-header flex-shrink-0">
                <span className="text-xl mr-2">
                    <ImageAdaptive src="/images/block/ir_transmitter.svg" baseWidth={40} />
                </span>
                <span className="text-color-white-base font-semibold text-sm">赤外線信号の設定</span>
            </div>

            {/* 信号を選択エリア（スクロール） */}
            <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3 flex flex-col gap-4">
                <div>
                    <p className="text-color-block-action-modal-text-muted text-xs mb-2">プリセット信号</p>
                    <div className="flex gap-2 flex-wrap">
                        {PRESETS.map((preset) => (
                            <button
                                key={preset.value}
                                onClick={() => setSelectedId(preset.value)}
                                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    selectedId === preset.value
                                        ? 'bg-color-block-action-modal-header text-color-white-base'
                                        : 'bg-color-block-action-modal-button-bg text-color-block-action-modal-text-muted hover:text-color-white-base'
                                }`}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <p className="text-color-block-action-modal-text-muted text-xs">学習済み信号</p>
                    {storedSignals.length === 0 && (
                        <div className="flex items-center justify-center py-4 text-color-block-action-modal-text-muted text-xs">
                            学習済みの赤外線信号がありません
                        </div>
                    )}
                    {[...storedSignals].reverse().map((signal, i) => (
                        <div
                            key={signal.id}
                            onClick={() => setSelectedId(signal.id)}
                            className="flex items-center rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                        >
                            <span className="bg-color-block-action-modal-row-bg text-color-block-action-modal-text-muted text-xs w-8 text-center self-stretch flex items-center justify-center flex-shrink-0">
                                {storedSignals.length - i}
                            </span>

                            {editingId === signal.id ? (
                                <div className="flex-1 bg-color-block-action-modal-row-bg px-2 py-1.5 flex items-center">
                                    <input
                                        type="text"
                                        value={editingName}
                                        onChange={(e) => setEditingName(e.target.value)}
                                        onBlur={() => handleRenameSubmit(signal.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRenameSubmit(signal.id);
                                            if (e.key === 'Escape') setEditingId(null);
                                        }}
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-full bg-color-block-action-modal-button-bg text-color-white-base border border-white/40 rounded px-2 py-1 text-xs focus:outline-none focus:border-white/80"
                                    />
                                </div>
                            ) : (
                                <span
                                    className={`flex-1 text-color-white-base text-xs px-3 py-2 transition-colors ${
                                        selectedId === signal.id
                                            ? 'bg-color-block-action-modal-header ring-1 ring-white/60'
                                            : 'bg-color-block-action-modal-row-bg hover:bg-color-block-action-modal-button-bg'
                                    }`}
                                >
                                    {signal.name}
                                </span>
                            )}

                            <span className="bg-color-block-action-modal-row-bg self-stretch flex items-center gap-1 px-2 flex-shrink-0">
                                {signal.source === 'manual' ? (
                                    <span className="text-color-block-action-modal-text-muted px-1">
                                        <IconAdaptive Icon={Keyboard} strokeWidth={2} baseSize={16} />
                                    </span>
                                ) : (
                                    <span
                                        className="rounded-sm flex-shrink-0 px-2"
                                        style={{
                                            width: 14,
                                            height: 14,
                                            backgroundColor: signal.source === 'auto-orange' ? '#FFBE00' : '#007BBD',
                                            margin: '0 4px',
                                        }}
                                    />
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingId(signal.id);
                                        setEditingName(signal.name);
                                    }}
                                    className="text-white/40 hover:text-white/80 px-1 transition-colors"
                                >
                                    <IconAdaptive Icon={Edit3} strokeWidth={2} baseSize={16} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedId === signal.id) setSelectedId(null);
                                        removeSignal(signal.id);
                                    }}
                                    className="text-white/40 hover:text-white/80 px-1 transition-colors"
                                >
                                    <IconAdaptive Icon={Trash2} strokeWidth={2} baseSize={16} />
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* 信号を学習エリア（線で区切って固定） */}
            <div className="px-4 pt-3 pb-3 border-t border-color-block-action-modal-border flex-shrink-0">
                <p className="text-color-block-action-modal-text-muted text-xs mb-2">信号を学習</p>
                <div className="flex gap-2 items-end">
                    <div className="flex flex-col gap-2 flex-1">
                        <input
                            type="text"
                            value={pendingName}
                            onChange={(e) => setPendingName(e.target.value)}
                            placeholder="信号名を入力"
                            className="w-full bg-color-block-action-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-xs placeholder:text-color-block-action-modal-text-muted focus:outline-none focus:border-white/50"
                        />
                        <textarea
                            value={pendingRawText}
                            onChange={(e) => setPendingRawText(e.target.value)}
                            placeholder="学習した信号を入力（例: 1300,350,1300,400,...）"
                            rows={3}
                            inputMode="numeric"
                            className="w-full bg-color-block-action-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-xs placeholder:text-color-block-action-modal-text-muted focus:outline-none focus:border-white/50 resize-none font-mono"
                        />
                    </div>
                    <button
                        onClick={handleAdd}
                        disabled={!canAdd}
                        className={`flex items-center gap-1 px-3 py-1.5 bg-color-block-action-modal-button-bg border border-dashed border-color-block-action-modal-text-muted rounded text-xs text-color-block-action-modal-text-muted disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-color-block-action-modal-text-muted disabled:hover:text-color-block-action-modal-text-muted hover:border-color-block-action-modal-header hover:text-color-block-action-modal-header transition-colors flex-shrink-0 ${
                            canAdd ? 'animate-pulse' : ''
                        }`}
                        style={
                            canAdd ? { borderColor: CATEGORIES.action.color, color: CATEGORIES.action.color } : {}
                        }
                    >
                        <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={14} />
                        信号を学習
                    </button>
                </div>
            </div>

            {/* フッター */}
            <div className="flex justify-end items-center gap-2 px-5 py-3 border-t border-color-block-action-modal-border flex-shrink-0">
                <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-color-block-action-modal-cancel text-color-block-action-modal-text-gray rounded-lg text-sm"
                >
                    キャンセル
                </button>
                <button
                    onClick={handleOk}
                    className="px-6 py-2 bg-color-block-action-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-action-modal-ok-hover transition-colors"
                >
                    OK
                </button>
            </div>
        </div>
    );
}
